import {ChangeDetectorRef, Component, Input} from '@angular/core';
import {IntegratedMasterEditorComponent} from "../../integrated-master-editor/integrated-master-editor.component";
import {GraphModel} from "../models/graph.model";
import {IntegratedMasterService} from "../../integrated-master-editor/services/integrated-master.service";
import {ProcessService} from "../../processes/process.service";
import {LeModalService, PropertiesQuery} from "common-shared";
import {ShowByValidConditionPipe} from "../../integrated-master-editor/show-by-valid-condition.pipe";
import {CopyPasteService, StateService} from "web-shared";
import {NgbPopover} from "@ng-bootstrap/ng-bootstrap";
import {GraphNodeModel} from "../models/graph-node.model";
import {GraphLinkModel} from "../models/graph-link.model";
import {EntityTypes} from "web-shared";
import {Subject} from "rxjs";
import * as shape from 'd3';
import {DagreNodesOnlyLayout, Orientation} from "../manufacturing-graph-modal/DagreNodesOnlyLayout";
import {ProcessesQuery} from "../../processes/process.store";
import {ItemsQuery} from "web-shared";
import {ResourcesQuery} from "../../resources/resource.store";

@Component({
  selector: 'app-graph-builder',
  templateUrl: './graph-builder.component.html'
})
export class GraphBuilderComponent extends IntegratedMasterEditorComponent {
  @Input() graph: GraphModel;
  @Input() entityId: string;
  update$: Subject<boolean> = new Subject();
  curve?: any = shape.curveBundle.beta(1);
  layout: any;
  EntityTypes = EntityTypes;

  constructor(public propertiesQuery: PropertiesQuery,
              public resourcesQuery: ResourcesQuery,
              public processesQuery: ProcessesQuery,
              public itemsQuery: ItemsQuery) {
    super();
    this.layout = new DagreNodesOnlyLayout();
    this.layout.defaultSettings = {
      orientation: Orientation.LEFT_TO_RIGHT,
      marginX: 20,
      marginY: 20,
      edgePadding: 10,
      rankPadding: 60,
      nodePadding: 30,
      curveDistance: 20
    };
  }

  togglePopover(popover: NgbPopover, node: GraphNodeModel, link?: GraphLinkModel) {
    if (popover.isOpen()) {
      popover.close();
    } else {
      popover.open({
        close: () => {
          popover.close();
        }, node: node, link: link
      });
    }
  }

  addProcessFromSource(sourceId: string, processId: string) {
    const newMo = this.addProcessAfter();
    newMo.ProcessId = processId;
    this.graph.Nodes.push(new GraphNodeModel({type: EntityTypes.MasterOperation, id: newMo.Id, entityId: newMo.Id, label: `${newMo.ProcessNumber.toString()}: ${this.processesQuery.getEntity(processId)?.Name}`}));
    this.graph.Links.push(new GraphLinkModel({source: newMo.Id, target: sourceId}));
    this.update$.next(true);
  }

  addResourceUsage(processNode: GraphNodeModel, resourceId: string) {
    const masterOperation = this.masterOperations.find(mo => mo.Id === processNode.entityId);
    const newMui = masterOperation.MasterUseInstructions.length === 1 && masterOperation.MasterUseInstructions[0].Resources.length === 0
      ? masterOperation.MasterUseInstructions[0]
      : this.integratedMasterService.addMasterUseInstructionAfter(masterOperation, masterOperation.getLastUseInstruction());
    const muiNode = new GraphNodeModel({type: EntityTypes.MasterUseInstruction, id: newMui.Id, entityId: newMui.Id, label: newMui.Code});
    this.graph.Nodes.push(muiNode);
    this.graph.Links.push(new GraphLinkModel({source: newMui.Id, target: processNode.id}));
    this.addResource(muiNode, resourceId);
    this.update$.next(true);
  }

  addResource(muiNode: GraphNodeModel, resourceId: string) {
    if (resourceId) {
      const masterUseInstruction = this.masterOperations.flatMap(mo => mo.MasterUseInstructions).find(mui => mui.Id === muiNode.entityId);
      masterUseInstruction.Resources = [...masterUseInstruction.Resources, resourceId];
      this.graph.Nodes.push(new GraphNodeModel({type: EntityTypes.Resource, id: resourceId, entityId: resourceId, label: this.resourcesQuery.getEntity(resourceId)?.DisplayName ?? 'unknown'}));
      this.graph.Links.push(new GraphLinkModel({source: resourceId, target: muiNode.id}));
      this.update$.next(true);
    }
  }

  addItemUsage(processNode: GraphNodeModel, itemId: string) {
    const masterOperation = this.masterOperations.find(mo => mo.Id === processNode.entityId);
    const newMii = this.integratedMasterService.addMasterInputInstructionAfter(masterOperation, masterOperation.getLastInputInstruction());
    const miiNode = new GraphNodeModel({type: EntityTypes.MasterInputInstruction, id: newMii.Id, entityId: newMii.Id, label: newMii.Code});
    this.graph.Nodes.push(miiNode);
    this.graph.Links.push(new GraphLinkModel({source: newMii.Id, target: processNode.id}));
    this.addItem(miiNode, itemId);
    this.update$.next(true);
  }

  addItem(miiNode: GraphNodeModel, itemId: string) {
    if (itemId) {
      const masterInputInstruction = this.masterOperations.flatMap(mo => mo.MasterInputInstructions).find(mii => mii.Id === miiNode.entityId);
      masterInputInstruction.ItemId = itemId;
      this.graph.Nodes.push(new GraphNodeModel({type: EntityTypes.Item, id: itemId, entityId: itemId, label: this.itemsQuery.getEntity(itemId)?.DisplayName ?? 'unknown'}));
      this.graph.Links.push(new GraphLinkModel({source: itemId, target: miiNode.id}));
      this.update$.next(true);
    }
  }
}
